import { ComparisonOperators } from '@enums/comparison-operators';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Gender } from '@enums/gender';
import { InputTypes } from '@enums/input-types';
import { LogicalOperators } from '@enums/logical-operators';
import { Pharmacy } from '@enums/pharmacies';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { ValidatorFunctions } from '@enums/validator-functions';
import { MedicalHistoryQuestion } from '@models/medical-history-question';
import { SelectableOption } from '@models/selectable-option';
import { TreatmentGenderSettings } from '@models/treatment-types/treatment-gender-settings.model';
import { TreatmentOrderAgreements } from '@models/treatment-types/treatment-order-agreements.model';
import { TreatmentType } from '@models/treatment-types/treatment-type.model';

/**
 * Defines the female condom treatment type.
 */
export const FemaleCondomTreatment = new TreatmentType({
  id: '24',
  name: ConsultationTreatmentTypes.FemaleCondom,
  defaultConsultationStatus: ConsultationStatus.Pending,
  designatedPharmacyId: Pharmacy.ZEAL_SPECIALTY,
  genderSettings: new TreatmentGenderSettings({
    disallowed: [Gender.Male],
  }),
  requiresInsuranceTypeSelection: true,
  orderPageHeaderSubtitle: 'We just need a few more details before completing your order.',
  orderAgreements: new TreatmentOrderAgreements(
    [
      'I understand FC2 should not be used with a diaphragm, cervical cap, sponge, or birth control ring.',
      'I acknowledge that my medical provider will exercise independent, professional judgement in my treatment and medical care. I further acknowledge that if my provider does not find that I am a candidate for prescription treatment, they will not prescribe me medication.',
      'I understand my prescription will be sent by mail and I consent to automatic refills and shipment until no refills remain. When needed, the pharmacy may contact my provider for new prescriptions. I understand that I should keep my information current and that I can cancel refills anytime for any reason by calling 1-888-412-ZEAL (9325) or emailing info@zealsp.com.',
    ],
    'The FC2 Female Condom is an FDA approved, hormone-free, latex-free method indicated for the protection against unintended pregnancy, STDs and HIV/AIDS. It is packaged in a 12-count box.',
  ),
  customMedicalHistoryQuestions: [
    new MedicalHistoryQuestion({
      id: 'nitrileProductReaction',
      type: InputTypes.Radio,
      label: 'Have you ever had a reaction to nitrile products?',
      validators: [
        {
          validatorFn: {
            name: ValidatorFunctions.Required,
          },
          errorMessage: 'Please select one of these options',
        },
        {
          validatorFn: {
            name: ValidatorFunctions.Pattern,
            params: [`^${QuestionnaireAnswers.No}$`],
          },
          errorMessage: 'This product may not be right for you. Please consult with your healthcare provider.',
        },
      ],
      options: [new SelectableOption(QuestionnaireAnswers.Yes), new SelectableOption(QuestionnaireAnswers.No)],
    }),
    new MedicalHistoryQuestion(
      {
        id: 'hasKnownAllergies',
        type: InputTypes.Radio,
        label: 'Do you have any allergies that you are aware of?',
        validators: [
          {
            validatorFn: {
              name: ValidatorFunctions.Required,
            },
            errorMessage: 'Please select one of these options',
          },
        ],
        options: [new SelectableOption(QuestionnaireAnswers.Yes), new SelectableOption(QuestionnaireAnswers.No)],
      },
      'allergies',
      'knownAllergiesDetails',
      false,
    ),
    new MedicalHistoryQuestion({
      id: 'knownAllergiesDetails',
      type: InputTypes.Textarea,
      placeholder: 'Type here',
      validators: [
        {
          validatorFn: {
            name: ValidatorFunctions.Required,
          },
          errorMessage: 'Please provide details',
        },
      ],
      visibility: {
        triggerQuestions: {
          questions: [
            {
              id: 'hasKnownAllergies',
              value: QuestionnaireAnswers.Yes,
              comparisonOperator: ComparisonOperators.Equal,
            },
          ],
          logicalOperator: LogicalOperators.And,
        },
        externalConditions: {
          conditions: [],
          logicalOperator: LogicalOperators.And,
        },
        logicalOperator: LogicalOperators.And,
      },
    }),
    new MedicalHistoryQuestion(
      {
        id: 'questionsForHealthcareProvider',
        type: InputTypes.Textarea,
        label: 'Do you have any questions for your healthcare provider?',
        placeholder: 'Type here',
        validators: [
          {
            validatorFn: {
              name: ValidatorFunctions.MaxLength,
              params: [300],
            },
            errorKey: 'maxlength',
            errorMessage: 'This field cannot exceed 300 characters',
          },
        ],
      },
      'health_info',
    ),
  ],
  isThirdPartyFulfilled: true,
});
